import { v4, validate as isUuid } from "uuid";
import type { FormItems } from "@/composables/forms/formTypes";
import type { AIEquipmentSuggestionOption } from "@/libs/api/aiEquipmentSuggestions";

export type RecommendationForms = Pick<
  FormItems,
  "equipment" | "pumps" | "housings" | "intakes" | "seals" | "cables"
> & Partial<Pick<FormItems, "parameters">>;

type RecordData = Record<string, unknown>;
const mapNames = ["pumps", "housings", "intakes", "seals", "cables"] as const;
const componentFields = [
  ["motor_id", "r_motor"],
  ["sensor_id", "r_sensor"],
  ["mle_id", "r_mle"],
  ["vsd_id", "r_vsd"],
  ["switchboard_id", "r_switchboard"],
  ["up_trans_id", "r_step_up_transformer"],
  ["down_trans_id", "r_step_down_transformer"]
] as const;
const xrefFields = {
  pumps: [
    "total_stages",
    "length_feet",
    "weight_pounds",
    "rate_correction_ratio",
    "head_correction_ratio",
    "brake_horsepower_correction_ratio",
    "use_high_strength_shaft"
  ],
  housings: ["stage_count", "length_inches"],
  intakes: ["use_high_strength_shaft"],
  seals: ["use_high_strength_shaft"],
  cables: ["length_feet"]
} as const;
const relations = {
  pumps: ["pump_id", "r_pump"],
  housings: ["housing_id", "r_housing"],
  intakes: ["intake_id", "r_intake"],
  seals: ["seal_id", "r_seal"],
  cables: ["cable_id", "r_cable"]
} as const;

function record(value: unknown): RecordData {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new Error(
      "The recommendation is missing its sizing equipment data. Generate it again."
    );
  }
  return value as RecordData;
}

function catalogSelection(
  source: RecordData,
  idKey: string,
  relationKey: string
) {
  const id = source[idKey];
  if (typeof id !== "string" || !isUuid(id)) {
    throw new Error(`The recommendation has no valid catalog ID for ${idKey}.`);
  }
  const catalog = record(source[relationKey]);
  if (catalog.id !== id)
    throw new Error(`The recommendation has inconsistent ${idKey} data.`);
  return { [idKey]: id, [relationKey]: JSON.parse(JSON.stringify(catalog)) };
}

function orderedIds(value: unknown, records: RecordData): string[] {
  if (
    !Array.isArray(value) ||
    value.some(id => typeof id !== "string" || !records[id]) ||
    new Set(value).size !== value.length ||
    value.length !== Object.keys(records).length
  ) {
    throw new Error(
      "The recommendation has incomplete equipment ordering. Generate it again."
    );
  }
  return value;
}

/** Populate the editor in one synchronous step after validating the whole package.
 * Save/Cancel, revision creation, offline queueing and report recalculation remain
 * owned by ScenarioDetail, just as for manually selected equipment.
 */
export function applySizingRecommendation(
  option: AIEquipmentSuggestionOption,
  forms: RecommendationForms
): void {
  const draft = option.sizingDraft;
  if (!option.traceId || !draft || ![1, 2].includes(draft.version)) {
    throw new Error(
      "This recommendation has no verified sizing payload. Generate it again."
    );
  }
  const equipmentId = forms.equipment.data.value.id;
  const parameters: Record<string, number> = {};
  if (draft.version === 2) {
    if (!forms.parameters) throw new Error("Operating parameter forms are required for this design.");
    const allowed = ["target_liquid_rate_std_barrel_day", "pump_hz", "minimum_frequency", "maximum_frequency"];
    for (const [field, value] of Object.entries(record(draft.parameters))) {
      if (!allowed.includes(field) || typeof value !== "number" || !Number.isFinite(value) || value <= 0)
        throw new Error(`Invalid recommended operating parameter: ${field}`);
      parameters[field] = value;
    }
    if (!parameters.pump_hz || !parameters.target_liquid_rate_std_barrel_day ||
        !parameters.minimum_frequency || !parameters.maximum_frequency ||
        parameters.minimum_frequency > parameters.pump_hz || parameters.pump_hz > parameters.maximum_frequency)
      throw new Error("The recommended operating envelope is invalid.");
  }
  if (!equipmentId)
    throw new Error("Save the scenario before applying a recommendation.");
  const sourceEquipment = record(draft.equipment);
  const equipment: RecordData = {};
  for (const [idKey, relationKey] of componentFields) {
    if (
      sourceEquipment[idKey] == null &&
      sourceEquipment[relationKey] == null &&
      (idKey !== "motor_id" || option.incomplete === true)
    ) {
      equipment[idKey] = null;
      equipment[relationKey] = null;
    } else {
      Object.assign(
        equipment,
        catalogSelection(sourceEquipment, idKey, relationKey)
      );
    }
  }
  equipment.use_vsd = sourceEquipment.use_vsd === true;
  if (equipment.use_vsd && !equipment.vsd_id && !option.incomplete)
    throw new Error("The recommended VSD is missing.");

  const ids: Record<string, string> = {};
  const maps: Record<string, Record<string, RecordData>> = {};
  for (const name of mapNames) {
    const source = record(draft[name]);
    maps[name] = {};
    for (const [key, value] of Object.entries(source)) {
      const xref = record(value);
      if (xref.id !== key || ids[key])
        throw new Error(
          "The recommendation has inconsistent equipment references."
        );
      ids[key] = v4();
      const [idKey, relationKey] = relations[name];
      const next: RecordData = {
        ...catalogSelection(xref, idKey, relationKey),
        __typename: `scenario_equipment_xref_${name}`,
        id: ids[key],
        winning_revision_id: null,
        journal_deleted_at: null,
        ...(name === "housings" ? {} : { scenario_equipment_id: equipmentId })
      };
      for (const field of xrefFields[name]) {
        if (field in xref) next[field] = xref[field];
      }
      if (name === "intakes" || name === "seals")
        next.use_high_strength_shaft ??= false;
      // Preserve unknown cable length as unknown: do not invent a verified layout.
      if (name === "cables") next.length_feet ??= null;
      maps[name][ids[key]] = next;
    }
    if (name !== "housings") {
      const order = orderedIds(sourceEquipment[`${name}_order`], source);
      equipment[`${name}_order`] = order.map(id => ids[id]);
      equipment[`r_xref_${name}`] = order.map(id => maps[name][ids[id]]);
    }
  }
  if (!Object.keys(maps.pumps).length && !option.incomplete)
    throw new Error("The recommended pump is missing.");
  const usedHousings = new Set<string>();
  for (const [key, xref] of Object.entries(draft.pumps)) {
    const housingIds = xref.housings_order;
    if (!Array.isArray(housingIds) || !housingIds.length)
      throw new Error("The recommended pump housings are missing.");
    const pump = maps.pumps[ids[key]];
    pump.housings_order = housingIds.map(id => {
      const housing = draft.housings[id];
      if (
        !housing ||
        housing.scenario_equipment_xref_pump_id !== key ||
        usedHousings.has(id)
      ) {
        throw new Error(
          "The recommendation has inconsistent pump housing references."
        );
      }
      if (
        typeof housing.stage_count !== "number" ||
        !Number.isInteger(housing.stage_count) ||
        housing.stage_count <= 0
      ) {
        throw new Error("The recommended housing stage count is invalid.");
      }
      usedHousings.add(id);
      maps.housings[ids[id]].scenario_equipment_xref_pump_id = ids[key];
      return ids[id];
    });
    pump.r_xref_housings = housingIds.map(id => maps.housings[ids[id]]);
    const stages = housingIds.reduce(
      (sum, id) => sum + (draft.housings[id].stage_count as number),
      0
    );
    if (pump.total_stages !== stages)
      throw new Error("The recommended pump and housing stages do not agree.");
  }
  if (usedHousings.size !== Object.keys(draft.housings).length)
    throw new Error("The recommendation contains unassigned housings.");

  // Keep saved xrefs as tombstones so the existing revision mutation deletes
  // them. Unsaved xrefs are simply replaced. Build every map before mutating.
  const replacements: Record<string, Record<string, RecordData>> = {};
  for (const name of mapNames) {
    replacements[name] = {};
    for (const [id, value] of Object.entries(forms[name].data.value)) {
      if (forms[name].initialData.value?.[id]) {
        replacements[name][id] = { ...value, journal_deleted_at: "now()" };
      }
    }
    Object.assign(replacements[name], maps[name]);
  }
  for (const name of mapNames) {
    // The checked API boundary above constructs the same xrefs as the manual
    // selectors, with catalog relationships available for immediate calculation.
    forms[name].data.value = replacements[name] as never;
  }
  forms.equipment.update(equipment);
  if (draft.version === 2) forms.parameters!.update(parameters);
}
